import React, { useState } from 'react';
import { useModalAnimation } from '../../hooks/useModalAnimation';
import { X, Download, CheckSquare, Square, FileSpreadsheet, Layers } from 'lucide-react';
import { ExportProfilesTab } from './ExportProfilesTab';
import { useToast } from '../../context/ToastContext';
import * as XLSX from 'xlsx';

export const ExportModal = ({ isOpen, onClose, filters, count, selectedProductIds }) => {
    const { isVisible, isClosing, handleAnimationEnd } = useModalAnimation(isOpen, onClose);
    const [selectedTabs, setSelectedTabs] = useState(['general']);
    const [format, setFormat] = useState('xlsx');
    const [exporting, setExporting] = useState(false);
    const [splitByProduct, setSplitByProduct] = useState(false);
    const [error, setError] = useState(null);
    const { toast } = useToast();

    const availableTabs = [
        { id: 'general', label: 'General Info' },
        { id: 'charges', label: 'Charges' },
        { id: 'properties', label: 'Properties' },
        { id: 'languages', label: 'Languages' },
        { id: 'packaging', label: 'Packaging' },
        { id: 'pictures', label: 'Pictures' },
        { id: 'software', label: 'Software & Resources' },
        { id: 'accessories', label: 'Accessories' }
    ];

    const toggleTab = (tabId) => {
        if (selectedTabs.includes(tabId)) {
            setSelectedTabs(selectedTabs.filter(t => t !== tabId));
        } else {
            setSelectedTabs([...selectedTabs, tabId]);
        }
    };

    const toggleAll = () => {
        if (selectedTabs.length === availableTabs.length) {
            setSelectedTabs([]);
        } else {
            setSelectedTabs(availableTabs.map(t => t.id));
        }
    };

    const [exportMode, setExportMode] = useState('standard'); // 'standard' or 'advanced'
    const [templateType, setTemplateType] = useState('logistics_excel');

    const handleExport = async () => {
        setExporting(true);
        setError(null);
        try {
            if (exportMode === 'advanced') {
                // Advanced Export Logic
                const payload = {
                    template_type: templateType,
                    product_ids: selectedProductIds && selectedProductIds.length > 0 ? selectedProductIds : [],
                    format: templateType.includes('pdf') ? 'pdf' : 'excel'
                };

                if (!payload.product_ids || payload.product_ids.length === 0) {
                    const params = new URLSearchParams();
                    if (filters.sku) params.append('sku', filters.sku);
                    if (filters.model) params.append('model', filters.model);
                    if (filters.ean) params.append('ean', filters.ean);
                    if (filters.category) params.append('category', filters.category);
                    if (filters.status) params.append('status', filters.status);

                    const idRes = await fetch(`/api/products/ids?${params.toString()}`, {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });
                    if (idRes.ok) {
                        const ids = await idRes.json();
                        payload.product_ids = ids;
                    }
                }

                const res = await fetch('/api/export/generate', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify(payload)
                });

                if (!res.ok) throw new Error(await res.text());

                const box = await res.blob();

                // Extract filename from Content-Disposition header
                const contentDisposition = res.headers.get('Content-Disposition');
                let filename = `Export_${templateType}_${new Date().toISOString().split('T')[0]}.${payload.format === 'excel' ? 'xlsx' : 'pdf'}`;

                if (contentDisposition) {
                    const match = contentDisposition.match(/filename="?([^"]+)"?/);
                    if (match && match[1]) {
                        filename = match[1];
                    }
                }

                const url = window.URL.createObjectURL(box);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                onClose();

            } else {
                // Standard Export Logic - Server Side Streaming
                const params = new URLSearchParams();
                if (filters.sku) params.append('sku', filters.sku);
                if (filters.model) params.append('model', filters.model);
                if (filters.ean) params.append('ean', filters.ean);
                if (filters.category) params.append('category', filters.category);
                if (filters.status) params.append('status', filters.status);
                if (filters.color) params.append('color', filters.color);
                if (filters.trace_id) params.append('trace_id', filters.trace_id);
                if (filters.property_name) params.append('property_name', filters.property_name);
                if (filters.property_value) params.append('property_value', filters.property_value);
                if (filters.language_id) params.append('language_id', filters.language_id);
                if (filters.language_id_2) params.append('language_id_2', filters.language_id_2);

                if (selectedProductIds && selectedProductIds.length > 0) {
                    params.append('product_ids', selectedProductIds.join(','));
                }

                params.append('format', format);
                params.append('splitByProduct', splitByProduct);
                params.append('token', localStorage.getItem('token'));

                // Trigger direct download
                window.location.href = `/api/export/standard?${params.toString()}`;

                onClose();
            }
        } catch (err) {
            console.error("Export error:", err);
            setError(err.message || "Failed to export data.");
        } finally {
            setExporting(false);
        }
    };

    if (!isVisible) return null;

    return (
        <div
            className={`fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center ${isClosing ? 'animate-out fade-out duration-200' : 'animate-in fade-in duration-200'}`}
            onAnimationEnd={handleAnimationEnd}
        >
            <div className={`bg-stone-900 border border-stone-800 rounded-xl shadow-2xl w-full max-w-7xl overflow-hidden flex flex-col h-[90vh] ${isClosing ? 'animate-out zoom-out-95 duration-200' : 'animate-in zoom-in-95 duration-200'}`}>
                {/* Header */}
                <div className="px-6 py-4 border-b border-stone-800 flex items-center justify-between bg-stone-950">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-900/20 rounded-lg">
                            <FileSpreadsheet className="text-green-400" size={20} />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white">Export Data</h3>
                            <p className="text-xs text-stone-400">
                                {selectedProductIds && selectedProductIds.length > 0
                                    ? `Exporting ${selectedProductIds.length} selected products`
                                    : `Exporting ${count} products matches`}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-stone-500 hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-8 flex-1 overflow-y-auto">
                    {/* Error Display */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-900/20 border border-red-800 rounded-lg text-red-200 text-sm">
                            <strong>Error:</strong> {error}
                        </div>
                    )}

                    {/* Export Mode Toggle */}
                    <div className="flex p-1 bg-stone-950 rounded-lg mb-8 border border-stone-800">
                        <button
                            onClick={() => setExportMode('standard')}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${exportMode === 'standard' ? 'bg-stone-800 text-white shadow-sm' : 'text-stone-500 hover:text-stone-300'}`}
                        >
                            Standard Export
                        </button>
                        <button
                            onClick={() => setExportMode('advanced')}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${exportMode === 'advanced' ? 'bg-blue-900/30 text-blue-400 shadow-sm border border-blue-900/50' : 'text-stone-500 hover:text-stone-300'}`}
                        >
                            Advanced Templates
                        </button>
                        <button
                            onClick={() => setExportMode('profiles')}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${exportMode === 'profiles' ? 'bg-purple-900/30 text-purple-400 shadow-sm border border-purple-900/50' : 'text-stone-500 hover:text-stone-300'}`}
                        >
                            Export Profiles
                        </button>
                    </div>

                    {exportMode === 'profiles' ? (
                        <div className="flex-1 min-h-0">
                            <ExportProfilesTab onSelectProfile={(profile) => {
                                // Execute Export with Profile
                                // Execute Export with Profile
                                let fields = [];

                                // New Structure: profile.config.sections
                                if (profile.config && profile.config.sections && Array.isArray(profile.config.sections)) {
                                    // Valid new config structure
                                    fields = profile.config.sections;
                                } else {
                                    // Fallback / Legacy
                                    try {
                                        if (profile.fields_config) {
                                            fields = typeof profile.fields_config === 'string'
                                                ? JSON.parse(profile.fields_config)
                                                : profile.fields_config;
                                        }
                                    } catch (e) {
                                        console.error("Error parsing profile config", e);
                                        toast.error("Invalid profile configuration format.");
                                        return;
                                    }
                                }

                                if (!fields || (Array.isArray(fields) && fields.length === 0)) {
                                    // It's possible to have a profile with 0 selected fields, but let's warn
                                    // However, don't block if the backend handles it or if it's just a newly created empty profile
                                    // toast.warning("This profile seems to have no fields selected.");
                                }

                                const params = new URLSearchParams();
                                if (filters.sku) params.append('sku', filters.sku);
                                if (filters.model) params.append('model', filters.model);
                                if (filters.ean) params.append('ean', filters.ean);
                                if (filters.category) params.append('category', filters.category);
                                if (filters.status) params.append('status', filters.status);
                                if (filters.color) params.append('color', filters.color);

                                if (selectedProductIds && selectedProductIds.length > 0) {
                                    params.append('product_ids', selectedProductIds.join(','));
                                }

                                // Use Profile Settings
                                params.append('format', profile.output_style || 'xlsx');
                                if (profile.template) {
                                    params.append('template', profile.template);
                                }

                                // Pass full granular config
                                if (profile.config && profile.config.sections) {
                                    params.append('profile_config', JSON.stringify(profile.config.sections));
                                } else if (fields && fields.length > 0) {
                                    // Fallback for legacy or if parsed from fields_config
                                    // If fields is array of strings (legacy)
                                    if (typeof fields[0] === 'string') {
                                        params.append('sections', fields.join(','));
                                    } else {
                                        // If it is the new structure but stored in fields_config
                                        params.append('profile_config', JSON.stringify(fields));
                                    }
                                }

                                const token = localStorage.getItem('token');
                                if (token) {
                                    params.append('token', token);
                                }

                                window.location.href = `/api/export/standard?${params.toString()}`;
                                onClose();
                            }} />
                        </div>
                    ) : exportMode === 'advanced' ? (
                        <div className="space-y-6">
                            <div className="p-4 bg-blue-900/10 border border-blue-900/30 rounded-lg">
                                <h4 className="text-blue-400 font-bold mb-2 flex items-center gap-2">
                                    <Layers size={18} />
                                    Premium Templates
                                </h4>
                                <p className="text-sm text-blue-200/70">
                                    Select a pre-defined template for professional reports.
                                    These templates handle complex layouts and specific formatting requirements.
                                </p>
                            </div>

                            <div>
                                <label className="text-sm font-bold text-stone-300 mb-3 block">Select Template</label>
                                <div className="grid grid-cols-1 gap-3">
                                    <label className={`flex items-center gap-4 cursor-pointer group p-4 rounded-xl border transition-all duration-200 ${templateType === 'logistics_excel' ? 'bg-stone-800 border-green-500/50' : 'bg-stone-950/30 border-stone-800 hover:bg-stone-800/30'}`}>
                                        <input type="radio" name="template" value="logistics_excel" checked={templateType === 'logistics_excel'} onChange={(e) => setTemplateType(e.target.value)} className="hidden" />
                                        <div className="p-3 bg-green-900/20 rounded-lg text-green-400">
                                            <FileSpreadsheet size={24} />
                                        </div>
                                        <div>
                                            <div className="font-bold text-stone-200">Logistics Report (Excel)</div>
                                            <div className="text-xs text-stone-500">Detailed logistics data including weights, dimensions, and customs codes.</div>
                                        </div>
                                        {templateType === 'logistics_excel' && <div className="ml-auto w-3 h-3 bg-green-500 rounded-full shadow-[0_0_10px_rgba(34,197,94,0.5)]" />}
                                    </label>

                                    <label className={`flex items-center gap-4 cursor-pointer group p-4 rounded-xl border transition-all duration-200 ${templateType === 'properties_excel' ? 'bg-stone-800 border-blue-500/50' : 'bg-stone-950/30 border-stone-800 hover:bg-stone-800/30'}`}>
                                        <input type="radio" name="template" value="properties_excel" checked={templateType === 'properties_excel'} onChange={(e) => setTemplateType(e.target.value)} className="hidden" />
                                        <div className="p-3 bg-blue-900/20 rounded-lg text-blue-400">
                                            <FileSpreadsheet size={24} />
                                        </div>
                                        <div>
                                            <div className="font-bold text-stone-200">Properties Report (Excel)</div>
                                            <div className="text-xs text-stone-500">Full list of product properties and technical specifications.</div>
                                        </div>
                                        {templateType === 'properties_excel' && <div className="ml-auto w-3 h-3 bg-blue-500 rounded-full shadow-[0_0_10px_rgba(59,130,246,0.5)]" />}
                                    </label>

                                    <label className={`flex items-center gap-4 cursor-pointer group p-4 rounded-xl border transition-all duration-200 ${templateType === 'datasheet_pdf' ? 'bg-stone-800 border-red-500/50' : 'bg-stone-950/30 border-stone-800 hover:bg-stone-800/30'}`}>
                                        <input type="radio" name="template" value="datasheet_pdf" checked={templateType === 'datasheet_pdf'} onChange={(e) => setTemplateType(e.target.value)} className="hidden" />
                                        <div className="p-3 bg-red-900/20 rounded-lg text-red-400">
                                            <Download size={24} />
                                        </div>
                                        <div>
                                            <div className="font-bold text-stone-200">Product Data Sheet (PDF)</div>
                                            <div className="text-xs text-stone-500">Professional product specification sheet with images and technical details.</div>
                                        </div>
                                        {templateType === 'datasheet_pdf' && <div className="ml-auto w-3 h-3 bg-red-500 rounded-full shadow-[0_0_10px_rgba(239,68,68,0.5)]" />}
                                    </label>

                                    <label className={`flex items-center gap-4 cursor-pointer group p-4 rounded-xl border transition-all duration-200 ${templateType === 'service_report' ? 'bg-stone-800 border-purple-500/50' : 'bg-stone-950/30 border-stone-800 hover:bg-stone-800/30'}`}>
                                        <input type="radio" name="template" value="service_report" checked={templateType === 'service_report'} onChange={(e) => setTemplateType(e.target.value)} className="hidden" />
                                        <div className="p-3 bg-purple-900/20 rounded-lg text-purple-400">
                                            <Layers size={24} />
                                        </div>
                                        <div>
                                            <div className="font-bold text-stone-200">Service Report (PDF)</div>
                                            <div className="text-xs text-stone-500">Software versions, language support, and resource links for service teams.</div>
                                        </div>
                                        {templateType === 'service_report' && <div className="ml-auto w-3 h-3 bg-purple-500 rounded-full shadow-[0_0_10px_rgba(168,85,247,0.5)]" />}
                                    </label>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Split Option for Multi-Selection */}
                            {selectedProductIds && selectedProductIds.length > 0 && selectedProductIds.length <= 10 && (
                                <div className="mb-6 p-4 bg-blue-900/20 border border-blue-800 rounded-lg">
                                    <label className="flex items-center gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={splitByProduct}
                                            onChange={(e) => setSplitByProduct(e.target.checked)}
                                            className="w-5 h-5 rounded border-stone-600 bg-stone-800 text-blue-500 focus:ring-blue-500 focus:ring-offset-stone-900"
                                        />
                                        <div>
                                            <span className="text-stone-200 font-bold block">Split Sheets by Product</span>
                                            <span className="text-xs text-stone-400">Creates separate sheets for each product (e.g., "SKU123 - General")</span>
                                        </div>
                                    </label>
                                </div>
                            )}

                            <div className="mb-8">
                                <label className="text-sm font-bold text-stone-300 mb-3 block">Format</label>
                                <div className="flex gap-6">
                                    <label className="flex items-center gap-3 cursor-pointer group p-3 rounded-lg border border-stone-800 hover:bg-stone-800/50 transition-colors flex-1">
                                        <input
                                            type="radio"
                                            name="format"
                                            value="xlsx"
                                            checked={format === 'xlsx'}
                                            onChange={(e) => setFormat(e.target.value)}
                                            className="hidden"
                                        />
                                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${format === 'xlsx' ? 'border-green-500' : 'border-stone-600 group-hover:border-stone-500'}`}>
                                            {format === 'xlsx' && <div className="w-2.5 h-2.5 bg-green-500 rounded-full" />}
                                        </div>
                                        <span className={format === 'xlsx' ? 'text-white font-medium' : 'text-stone-400'}>.xlsx (Excel)</span>
                                    </label>
                                    <label className="flex items-center gap-3 cursor-pointer group p-3 rounded-lg border border-stone-800 hover:bg-stone-800/50 transition-colors flex-1">
                                        <input
                                            type="radio"
                                            name="format"
                                            value="csv"
                                            checked={format === 'csv'}
                                            onChange={(e) => setFormat(e.target.value)}
                                            className="hidden"
                                        />
                                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${format === 'csv' ? 'border-green-500' : 'border-stone-600 group-hover:border-stone-500'}`}>
                                            {format === 'csv' && <div className="w-2.5 h-2.5 bg-green-500 rounded-full" />}
                                        </div>
                                        <span className={format === 'csv' ? 'text-white font-medium' : 'text-stone-400'}>.csv (Comma Separated)</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <label className="text-sm font-bold text-stone-300">Sheets / Tabs to Export</label>
                                    <button
                                        onClick={toggleAll}
                                        className="text-xs text-blue-400 hover:text-blue-300 transition-colors font-medium"
                                    >
                                        {selectedTabs.length === availableTabs.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 gap-3 p-1">
                                    {availableTabs.map(tab => (
                                        <div
                                            key={tab.id}
                                            onClick={() => toggleTab(tab.id)}
                                            className={`flex items-center gap-4 cursor-pointer group p-4 rounded-xl border transition-all duration-200 ${selectedTabs.includes(tab.id) ? 'bg-stone-800/80 border-stone-700' : 'bg-stone-950/30 border-stone-800 hover:bg-stone-800/30 hover:border-stone-700'}`}
                                        >
                                            <div className={`text-stone-500 group-hover:text-stone-300 transition-colors`}>
                                                {selectedTabs.includes(tab.id) ? <CheckSquare size={22} className="text-green-500" /> : <Square size={22} />}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-lg ${selectedTabs.includes(tab.id) ? 'bg-stone-700 text-stone-200' : 'bg-stone-900 text-stone-500'}`}>
                                                    <Layers size={18} />
                                                </div>
                                                <span className={`text-base font-medium ${selectedTabs.includes(tab.id) ? 'text-white' : 'text-stone-400'}`}>
                                                    {tab.label}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-stone-800 bg-stone-950 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg bg-stone-800 text-stone-300 hover:bg-stone-700 transition-colors text-sm font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleExport}
                        disabled={selectedTabs.length === 0 || exporting}
                        className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-500 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {exporting ? (
                            <>Processing...</>
                        ) : (
                            <>
                                <Download size={16} />
                                Download
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
